import React, { useState } from "react";
import "./PersonalInfo.scss";
import {
  Avatar,
  Button,
  Checkbox,
  Col,
  Flex,
  Input,
  Row,
  Image,
} from "antd";
import { faIdCard } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { IUserAddress, IUserInfo } from "@utils/Interfaces";
import userApi from "services/UserApi";
import Helpers from "@utils/Helpers";
import Validates from "@utils/Validates";
import { useUser } from "contexts/UserContext";
import Gallery from "@containers/Gallery/Gallery";
import { errorLog } from "logging";
import crowd from "@assets/icons/crown.svg";
import { useAuth } from "contexts/AuthContext";
import ImageCropper from "@components/ImageCropper/ImageCropper";
import { GalleryApi } from "services/GalleryApi";

interface IPersonalInfo {
  user: IUserInfo;
  setUser: (user: IUserInfo & IUserAddress) => void;
  setIsUpdating: (is: boolean) => void;
  isUpdating: boolean;
}

const PersonalInfo: React.FC<IPersonalInfo> = ({
  user,
  setUser,
  setIsUpdating,
  isUpdating,
}) => {
  const { updateUserProfile, userAddresses } = useUser();
  const { setLoading } = useAuth();
  const [editUser, setEditUser] = useState<IUserInfo>({
    ...user,
  });
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isOpenCropper, setIsOpenCropper] = useState(false);
  const [avatarForCrop, setAvatarForCrop] = useState("");

  const handleChangeEditUser = (key: string, value: any) => {
    if (key === "phone") {
      value = Validates.validateNumberForm(value);
    }
    setEditUser({ ...editUser, [key]: value });
  };

  const handleUpdate = async () => {
    if (editUser.type_ids.length === 0) {
      Helpers.notification.warning("You must belong to at least one role");
      return;
    }
    setIsUpdating(true);
    const { farm_info, user_id, google_id, ...userProfile } = {
      ...editUser,
    };
    try {
      const response = await userApi.updateUserProfile(userProfile);
      if (response.status === 200) {
        handleUpdateSuccess(
          response.data.data.access_token,
          response.data.data.refresh_token
        );
      }
    } catch (error) {
      setIsUpdating(false);
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("PersonalInfo.tsx - handleUpdate", error);
    }
  };

  const handleUpdateSuccess = (access: string, refresh: string) => {
    const userInfo = Helpers.decodeToken(access);
    setEditUser(userInfo.user);
    setUser(userInfo.user);
    updateUserProfile(access, refresh);
    setIsUpdating(false);
    Helpers.showSuccessUpdated("Update profile successfully");
  };

  const handleSelectAvatar = (avatarLst: any) => {
    if (avatarLst.length > 1) {
      Helpers.notification.warning(
        "Only one picture can be selected as an avatar. Please choose a single image."
      );
      return;
    }
    setAvatarForCrop(avatarLst[0]);
    setIsOpenCropper(true);
  };

  const handleCropAvatarComplete = async (croppedImg: any, documentId: any) => {
    setLoading(true);
    const response = await fetch(croppedImg);
    const blob = await response.blob();
    const file = new File([blob], `profile-crop-${documentId}.jpg`, {
      type: blob.type,
    });

    const formData = new FormData();
    formData.append("gallery", file);
    formData.append("gallery_type", "profile");
    formData.append("gallery_is_crop", "true");
    try {
      const response = await GalleryApi.uploadMedia(formData);
      if (response.status === 200) {
        setLoading(false);
        setIsOpenCropper(false);
        setEditUser({ ...editUser, avatar: response.data.data[0].url });
      }
    } catch (error) {
      Helpers.notification.error("Can not upload image");
      errorLog("GalleryContent.tsx - handleUpload", error);
    }
  };

  const handleChangeRole = (role_id: number, isChecked: boolean) => {
    if (isChecked) {
      const newRoles = [...editUser.type_ids, role_id];
      setEditUser({ ...editUser, type_ids: newRoles });
    } else {
      const newRoles = [...editUser.type_ids];
      const filteredRole = newRoles.filter((item) => item !== role_id);
      setEditUser({ ...editUser, type_ids: filteredRole });
    }
  };

  return (
    <div className="PersonalInfo">
      <div className="section head-section">
        <Flex align="center" gap={12}>
          <div className="avatar-wrap" onClick={() => setIsGalleryOpen(true)}>
            <div className="avatar-overlay">Edit</div>
            {editUser?.avatar ? (
              <Avatar size={100} src={editUser?.avatar} />
            ) : (
              <Avatar className="user-avatar">
                {user?.first_name[0].toUpperCase()}
              </Avatar>
            )}
          </div>
          <div className="main-head">
            <div className="user-name">{user?.display_name}</div>
            <div className="user-id">
              <FontAwesomeIcon icon={faIdCard} color="#378440" />
              <span>{user.email}</span>
            </div>
            <div className="user-location">
              <FontAwesomeIcon icon={faLocationDot} color="#378440" />
              <span>
                {
                  userAddresses?.find(
                    (item: any) => item.address_id === user.address_id
                  )?.street
                }
              </span>
            </div>
          </div>
          <div className="role-radio-group">
            <div
              className={`radio-item ${
                editUser.type_ids.includes(1) && "active"
              }`}
              onClick={() => {
                if (editUser.type_ids.includes(1)) {
                  handleChangeRole(1, false);
                } else {
                  handleChangeRole(1, true);
                }
              }}
            >
              <div className="role-title">Consumer</div>
              <Image
                className="item-image"
                src={
                  "https://wewin.com.vn/wp-content/uploads/2023/08/customer-consumer2.png"
                }
                preview={false}
                width={80}
                height={100}
              />
              <Checkbox
                className="item-checkbox"
                checked={editUser.type_ids.includes(1)}
                value={1}
                onChange={(e) =>
                  handleChangeRole(e.target.value, e.target.checked)
                }
              ></Checkbox>
            </div>
            <div
              className={`radio-item ${
                editUser.type_ids.includes(2) && "active"
              }`}
              onClick={() => {
                if (editUser.type_ids.includes(2)) {
                  handleChangeRole(2, false);
                } else {
                  handleChangeRole(2, true);
                }
              }}
            >
              <div className="role-title">Producer</div>
              <Image
                className="item-image"
                src={
                  "https://kamereo.vn/blog/wp-content/uploads/2024/06/nha-cung-cap-la-gi-1.jpg"
                }
                preview={false}
                width={80}
                height={100}
              />
              <Checkbox
                className="item-checkbox"
                checked={editUser.type_ids.includes(2)}
                value={2}
                onChange={(e) =>
                  handleChangeRole(e.target.value, e.target.checked)
                }
              ></Checkbox>
            </div>
          </div>
        </Flex>
      </div>
      <div className="section personal-section">
        <div className="section-title">Personal Information</div>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                First name<span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={editUser.first_name}
                onChange={(e) =>
                  handleChangeEditUser("first_name", e.target.value)
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                Last name<span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={editUser.last_name}
                onChange={(e) =>
                  handleChangeEditUser("last_name", e.target.value)
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Profile name</div>
              <Input
                type="email"
                size="large"
                variant="filled"
                value={editUser.display_name}
                disabled
                suffix={<img src={crowd} width={14} alt="email" />}
                onChange={(e) =>
                  handleChangeEditUser("display_name", e.target.value)
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Phone</div>
              <Input
                size="large"
                variant="filled"
                value={editUser.phone || ""}
                onChange={(e) => handleChangeEditUser("phone", e.target.value)}
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className="form-submit">
        <Button
          className="save-btn"
          disabled={isUpdating}
          onClick={handleUpdate}
        >
          Save
        </Button>
      </div>
      <Gallery
        isGalleryOpen={isGalleryOpen}
        submitGallery={handleSelectAvatar}
        cancelGallery={() => setIsGalleryOpen(false)}
        limit={1}
        activeKey="1"
      />
      <ImageCropper
        isOpenCropper={isOpenCropper}
        closeCropper={() => setIsOpenCropper(false)}
        selectedImg={avatarForCrop}
        onComplete={handleCropAvatarComplete}
        type="avatar"
      />
    </div>
  );
};

export default PersonalInfo;
